import { MACHINE_API_URLS } from "@/config/apiConfig";
import { MachineResponse } from "@/types/machine/machine";

const getAuthToken = (): string => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("token") || "";
};

export const getAllMachines = async (): Promise<MachineResponse[]> => {
  const token = getAuthToken();
  const response = await fetch(`${MACHINE_API_URLS.GET_ALL_MACHINES}?limit=500`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Authorization: token },
  });
  if (!response.ok) throw new Error("Failed to get all machines");
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};

export const getMachineById = async (name: string): Promise<MachineResponse> => {
  const token = getAuthToken();
  const response = await fetch(
    `${MACHINE_API_URLS.GET_MACHINE_BY_ID}?name=${encodeURIComponent(name)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: token },
    }
  );
  if (!response.ok) throw new Error("Failed to get machine by id");
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const getMachineByDeviceEui = async (deviceEui: string): Promise<MachineResponse | null> => {
  const token = getAuthToken();
  const response = await fetch(
    `${MACHINE_API_URLS.GET_MACHINE_BY_DEVICE_EUI}?device_eui=${encodeURIComponent(deviceEui)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: token },
    }
  );
  if (!response.ok) throw new Error("Failed to get machine by device_eui");
  const result = await response.json();
  return (result?.data?.item as MachineResponse) ?? null;
};

export const createMachine = async (machine: any): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(MACHINE_API_URLS.CREATE_MACHINE, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify(machine),
  });
  if (!response.ok) throw new Error("Failed to create machine");
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const updateMachine = async (name: string, machine: any): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(MACHINE_API_URLS.UPDATE_MACHINE, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify({ name, ...machine }),
  });
  if (!response.ok) throw new Error("Failed to update machine");
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const deleteMachine = async (name: string): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(MACHINE_API_URLS.DELETE_MACHINE, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error("Failed to delete machine");
  const result = await response.json();
  return result?.data ?? result;
};

export const getAllMachinesAndPrices = async (): Promise<any> => {
  const token = getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = token;
  const response = await fetch(`${MACHINE_API_URLS.GET_ALL_MACHINES_AND_PRICES}?limit=500`, {
    method: "GET",
    headers,
  });
  if (!response.ok) {
    throw new Error(`Failed to get all machines and prices: ${response.status} ${response.statusText}`);
  }
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : result?.data ?? result;
};
